import { PersonSelect } from '../ui/PersonSelect';
import type { CalendarFilters as Filters, CalendarEventKind } from '../../services/calendar/calendarAggregate';
import { UNTYPED_COLORS, type AgendaColorMode, type ColorMap } from '../../services/calendar/eventColors';
import { useEventTypes } from '../../hooks/queries/useEventTypes';
import { useTaskTemplates } from '../../hooks/queries/useTaskTemplates';
import { cn } from '../../lib/cn';
import { useDossiers } from '../../hooks/queries/useDossiers';
import { useEffectiveDossierFields } from '../../hooks/queries/useCabinetSettings';

const KIND_LABEL: Record<CalendarEventKind, string> = {
  hearing: 'Audiences',
  task: 'Tâches',
  prescription: 'Prescriptions',
  appointment: 'Rendez-vous',
};
const ALL_KINDS: CalendarEventKind[] = ['hearing', 'task', 'prescription', 'appointment'];

interface Props {
  filters: Filters;
  onChange: (next: Filters) => void;
  colorMode: AgendaColorMode;
  onColorMode: (mode: AgendaColorMode) => void;
  colors: ColorMap;
}

const UNTYPED_LABEL: Record<CalendarEventKind, string> = {
  hearing: 'Autre audience',
  appointment: 'Autre rendez-vous',
  task: 'Autre tâche',
  prescription: 'Prescription',
};

const COLOR_MODES: { value: AgendaColorMode; label: string }[] = [
  { value: 'type', label: 'Par type' },
  { value: 'dossier', label: 'Par dossier' },
];

export function CalendarFilters({ filters, onChange, colorMode, onColorMode, colors }: Props) {
  const { data: eventTypes = [] } = useEventTypes();
  const { data: templates = [] } = useTaskTemplates(true);
  const legend: { key: string; label: string; color: string }[] = [];
  for (const kind of ['hearing', 'appointment'] as const) {
    for (const t of eventTypes) {
      if (t.family === kind && t.is_active) legend.push({ key: t.id, label: t.name, color: colors[t.id] ?? t.color });
    }
    legend.push({ key: kind, label: UNTYPED_LABEL[kind], color: UNTYPED_COLORS[kind] });
  }
  for (const t of templates) {
    const color = colors[t.id];
    if (color) legend.push({ key: t.id, label: t.content, color });
  }
  legend.push({ key: 'task', label: legend.some((l) => templates.some((t) => t.id === l.key)) ? UNTYPED_LABEL.task : 'Tâche', color: UNTYPED_COLORS.task });

  const dossiersQuery = useDossiers({ include_closed: false });
  const { prescription } = useEffectiveDossierFields();
  const kinds = prescription ? ALL_KINDS : ALL_KINDS.filter((k) => k !== 'prescription');

  const toggleKind = (k: CalendarEventKind) => {
    const has = filters.kinds.includes(k);
    onChange({ ...filters, kinds: has ? filters.kinds.filter((x) => x !== k) : [...filters.kinds, k] });
  };

  return (
    <aside className="flex w-56 flex-shrink-0 flex-col gap-5 border-r border-[#e6e2d8] bg-[#fbfaf7] p-4">
      <div>
        <span className="text-[11px] font-semibold uppercase tracking-[0.08em] text-cime-text-muted">Avocats</span>
        <div className="mt-1.5">
          <PersonSelect
            fluid
            label="avocats"
            emptyLabel="Tous les avocats"
            value={filters.lawyerIds}
            onChange={(lawyerIds) => onChange({ ...filters, lawyerIds })}
          />
        </div>
      </div>

      <div>
        <label htmlFor="cal-dossier" className="text-[11px] font-semibold uppercase tracking-[0.08em] text-cime-text-muted">
          Dossier
        </label>
        <select
          id="cal-dossier"
          className="mt-1.5 h-[34px] w-full rounded-lg border border-neutral-300 bg-white px-2 text-[13px] text-cime-text-primary"
          value={filters.dossierId ?? ''}
          onChange={(e) => onChange({ ...filters, dossierId: e.target.value || null })}
        >
          <option value="">Tous</option>
          {(dossiersQuery.data ?? []).map((d) => (
            <option key={d.id} value={d.id}>
              {d.rg_number ? `${d.rg_number} — ${d.title}` : d.title}
            </option>
          ))}
        </select>
      </div>

      <div>
        <span className="text-[11px] font-semibold uppercase tracking-[0.08em] text-cime-text-muted">Type</span>
        <div className="mt-1.5 flex flex-col gap-1.5">
          {kinds.map((k) => (
            <label key={k} className="flex cursor-pointer items-center gap-2 text-[13px] hover:text-cime-primary">
              <input
                type="checkbox"
                className="m-0 h-3.5 w-3.5 flex-shrink-0 accent-cime-primary"
                checked={filters.kinds.includes(k)}
                onChange={() => toggleKind(k)}
              />
              {KIND_LABEL[k]}
            </label>
          ))}
        </div>
      </div>

      <div>
        <span className="text-[11px] font-semibold uppercase tracking-[0.08em] text-cime-text-muted">Couleurs</span>
        <div role="group" aria-label="Couleurs de l’agenda" className="mt-1.5 flex gap-0.5 rounded-cime bg-[rgba(4,52,41,0.05)] p-[3px]">
          {COLOR_MODES.map((m) => (
            <button
              key={m.value}
              type="button"
              aria-pressed={colorMode === m.value}
              onClick={() => onColorMode(m.value)}
              className={cn(
                'h-7 flex-1 rounded-md text-xs transition-colors',
                colorMode === m.value
                  ? 'bg-white font-semibold text-cime-primary shadow-[0_1px_2px_rgba(0,0,0,0.08)]'
                  : 'text-cime-text-muted hover:text-cime-primary',
              )}
            >
              {m.label}
            </button>
          ))}
        </div>
        {colorMode === 'type' ? (
          <ul className="mt-2.5 flex flex-col gap-1.5 text-[13px]">
            {legend.map((l) => (
              <li key={l.key} className="flex min-w-0 items-center gap-2">
                <span className="inline-block h-2.5 w-2.5 flex-shrink-0 rounded-[3px]" style={{ background: l.color }} aria-hidden />
                <span className="truncate">{l.label}</span>
              </li>
            ))}
            {prescription ? (
              <li className="flex items-center gap-2">
                <span className="inline-block h-2.5 w-2.5 flex-shrink-0 rounded-[3px]" style={{ background: UNTYPED_COLORS.prescription }} aria-hidden />
                {UNTYPED_LABEL.prescription}
              </li>
            ) : null}
          </ul>
        ) : (
          <p className="mt-2 text-xs text-cime-text-muted">Chaque dossier a sa teinte.</p>
        )}
      </div>
    </aside>
  );
}
